import axios from 'axios'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'api/v2/admins/outbound/deals'

const OutboundDealsApi = {
  /**
   * Get all OR filtered Deals
   * Invoke `GET admins/outbound/deals
   * @param {String} q - query to search by deal or payment reference numbers
   */
  get: async (rawParams = {}) => {
    const defaults = {
      page     : 1,
      per_page : 10,
      sort_by  : 'deals.created_at',
      sort_dir : 'desc',
      scope    : 'need_packing', // align with frontend default
    }

    // Merge and drop empty keys so only real filters go to the server
    const params = ParamsUtil.removeEmptyAttributes({ ...defaults, ...rawParams })

    return axios.get(API_PATH, { params })
  },

  /**
   * Get specific Deal
   * Invoke `GET admins/outbound/deals/:id
   * @param {Number} dealId - id of Deal
   */
  complete: async ({ dealId }) => axios.post(`${API_PATH}/${dealId}/complete`),

  show: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Log Invoice / Customer Receipt print request for audit trail
   * Invoke POST admins/outbound/deals/:id/log_print
   */
  logPrint: async ({ dealId, documentType, displayHeaderAndFooter = true }) => {
    const path = `${API_PATH}/${dealId}/log_print`

    const response = await axios.post(path, {
      document_type              : documentType,
      display_header_and_footer : displayHeaderAndFooter
    })

    return response
  },

  /**
   * Update Deal packing data
   * Invoke `GET admins/outbound/deals/:id/update_packing
   * @param {Number} dealId - id of Deal
   * @param {Date} pickup_at - Estimated pick up date of the package
   * @param {Boolean} invoice_done - whether invoice is included in the packaging
   * @param {Boolean} polaroid_done - whether polaroid is included in the packaging
   * @param {Boolean} love_letter_done - whether loveletter is included in the packaging
   * @param {Boolean} ready_to_ship - whether the whole Deal is ready to be shipped
   * @param {Array<Object>} cart_item_updates - id of CartItem and _done fields included
   */
  updatePacking: async ({
      dealId,
      pickup_at,
      invoice_done,
      polaroid_done,
      love_letter_done,
      ready_to_ship,
      cart_item_updates,
    }) => {
    const path = `${API_PATH}/${dealId}/update_packing`
    const params = {
      pickup_at,
      invoice_done,
      polaroid_done,
      love_letter_done,
      ready_to_ship,
      cart_item_updates,
    }

    const response = await axios.post(path, params)

    return response
  },

  /**
   * Update Deal shipment data
   * Invoke `GET admins/outbound/deals/:id/update_shipment
   * @param {Number} dealId - id of Deal
   * @param {String} tracking_number - nomor resi
   * @param {File} proof_of_shipping_image - bukti pengiriman
   */
  updateShipment: async ({ dealId, tracking_number, proof_of_shipping_image }) => {
    const path = `${API_PATH}/${dealId}/update_shipment`
    const formData = new FormData()
    formData.append('tracking_number', tracking_number)
    if (proof_of_shipping_image) {
      formData.append('proof_of_shipping_image', proof_of_shipping_image)
    }

    const response = await axios.post(path, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response
  },

  /**
   * Order Pickup for Deal (for example using Biteship)
   * Invoke `GET admins/outbound/deals/:id/order_pickup
   * @param {Number} dealId - id of Deal
   */
  orderPickup: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}/order_pickup`

    const response = await axios.post(path)

    return response
  },
  
  /**
   * Get history list of a deal
   * Invoke `GET admins/sales/deals/:id/list_history
   * @param {Number} dealId - id of Deal
   */
  listHistory: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}/list_history`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get specific history of a deal
   * Invoke `GET admins/sales/deals/get_history
   * @param {Number} historyId - id of Deal
   */
  getHistory: async ({ historyId }) => {
    const path = `${API_PATH}/get_history?history_id=${historyId}`

    const response = await axios.get(path)

    return response
  },

  previewSoldItemsCsv: async (file, options = {}) => {
    const path = `${API_PATH}/preview_sold_items_csv`
    const payload = new FormData()

    payload.append('file', file)
    payload.append('date_deal_created_from', options.date_deal_created_from || '')
    payload.append('date_deal_created_to', options.date_deal_created_to || '')
    payload.append('tz', options.tz || 'Asia/Jakarta')

    return axios.post(path, payload)
  },

  exportSoldItemsCsv: async (file, options = {}) => {
    const path = `${API_PATH}/export_sold_items_csv`
    const payload = new FormData()

    payload.append('file', file)
    payload.append('date_deal_created_from', options.date_deal_created_from || '')
    payload.append('date_deal_created_to', options.date_deal_created_to || '')
    payload.append('tz', options.tz || 'Asia/Jakarta')

    return axios.post(path, payload, { responseType: 'blob' })
  },
}

export default OutboundDealsApi
